<form [formGroup]="stationForm" class="atp">
  <header class="atp-head">
    <button type="button" class="atp-close" (click)="cancel()" aria-label="Close">
      <i class="bx bx-x" aria-hidden="true"></i>
    </button>
    <h2 class="atp-title">
      @if (station) {
        Edit {{ station.station_name }}
      } @else {
        Add New Station
      }
    </h2>
    <div class="atp-actions">
      <button type="button" class="atp-cancel" (click)="cancel()">Cancel</button>
      <button
        type="button"
        class="atp-save"
        [disabled]="stationForm.invalid || isDuplicate"
        (click)="save()"
      >
        Save Station
      </button>
    </div>
  </header>

  <div class="atp-body">
    <!-- Basic information -->
    <section class="atp-card">
      <header class="atp-card-head">
        <span class="atp-card-icon"><i class="bx bx-file" aria-hidden="true"></i></span>
        <div class="atp-card-headings">
          <h3 class="atp-card-title">Basic Information</h3>
          <p class="atp-card-sub">Provide initial identifying information for this station.</p>
        </div>
      </header>

      <label class="atp-label" for="stationName">Station Name *</label>
      <input
        id="stationName"
        class="atp-input"
        type="text"
        autocomplete="off"
        placeholder="e.g. Main Bar Station"
        formControlName="station_name"
        (input)="checkDuplicate()"
        [class.input-error]="
          isDuplicate ||
          (stationForm.get('station_name')?.invalid && stationForm.get('station_name')?.touched)
        "
      />
      @if (
        stationForm.get('station_name')?.touched &&
        stationForm.get('station_name')?.errors?.['required']
      ) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Station name cannot be empty.</p>
        </div>
      }
      @if (
        stationForm.get('station_name')?.touched &&
        stationForm.get('station_name')?.errors?.['minlength']
      ) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Station name must be at least 3 characters.</p>
        </div>
      }
      @if (stationForm.get('station_name')?.errors?.['maxlength']) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Station name cannot exceed 50 characters.</p>
        </div>
      }
      @if (isDuplicate) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Station name already exists in the system.</p>
        </div>
      }

      <label class="atp-label atp-label--spaced" for="stationDescription">Description</label>
      <textarea
        id="stationDescription"
        class="atp-input atp-textarea"
        rows="3"
        placeholder="Describe the purpose of this station..."
        formControlName="description"
      ></textarea>
      <p class="atp-help">Brief description of what this station is used for</p>
      @if (stationForm.get('description')?.errors?.['maxlength']) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Description cannot exceed 250 characters.</p>
        </div>
      }
    </section>

    <!-- Configuration -->
    <section class="atp-card">
      <header class="atp-card-head">
        <span class="atp-card-icon atp-card-icon--warn">
          <i class="bx bx-cog" aria-hidden="true"></i>
        </span>
        <div class="atp-card-headings">
          <h3 class="atp-card-title">Configuration</h3>
          <p class="atp-card-sub">Set operational rules and defaults for order allocation.</p>
        </div>
      </header>

      <label class="atp-label" for="stationType">Station Type *</label>
      <select
        id="stationType"
        class="atp-select"
        [class.atp-select--empty]="!stationForm.get('station_type')?.value"
        [value]="stationForm.get('station_type')?.value"
        (change)="selectType($any($event.target).value)"
      >
        <option value="" disabled>Select a station type</option>
        @for (type of stationTypes; track type.value) {
          <option [value]="type.value">{{ type.displayName }}</option>
        }
      </select>
      <p class="atp-help">Choose from the system-defined station types</p>

      <div class="atp-setting-row atp-setting-row--spaced">
        <div>
          <p class="atp-setting-title">Set as Default Station</p>
          <p class="atp-setting-sub">
            Default station will be pre-selected when creating new orders
          </p>
        </div>
        <div class="form-check form-switch atp-switch">
          <input
            class="form-check-input"
            type="checkbox"
            role="switch"
            id="isDefault"
            formControlName="is_default"
            (change)="checkDefaultClash()"
            aria-label="Set as default station"
          />
        </div>
      </div>
      @if (displayDuplicateDefault) {
        <div class="d-flex mt-2 errorText errorText--warn gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>
            This station type already has a default station. Saving will make this the default and
            remove the previous one.
          </p>
        </div>
      }
    </section>

    <!-- Equipment & connectivity -->
    <section class="atp-card">
      <header class="atp-card-head">
        <span class="atp-card-icon atp-card-icon--info">
          <i class="bx bx-plug" aria-hidden="true"></i>
        </span>
        <div class="atp-card-headings">
          <h3 class="atp-card-title">Equipment &amp; Connectivity</h3>
          <p class="atp-card-sub">Map network details and printing resources.</p>
        </div>
      </header>

      <label class="atp-label" for="assignedPrinter">Assigned Printer</label>
      <select
        id="assignedPrinter"
        class="atp-select"
        [class.atp-select--empty]="!stationForm.get('assigned_printer')?.value"
        formControlName="assigned_printer"
      >
        <option value="">Select a printer</option>
        @for (printer of printers; track printer.device_id) {
          <option [value]="printer.device_id">{{ printer.device_name }}</option>
        }
      </select>
      <p class="atp-help">Printer used for receipts and order tickets at this station</p>

      <label class="atp-label atp-label--spaced" for="ipAddress">IP Address</label>
      <input
        id="ipAddress"
        class="atp-input"
        type="text"
        autocomplete="off"
        placeholder="e.g. 192.168.1.100"
        formControlName="ip_address"
        [class.input-error]="
          stationForm.get('ip_address')?.invalid && stationForm.get('ip_address')?.touched
        "
      />
      <p class="atp-help">Network address for this station</p>
      @if (stationForm.get('ip_address')?.touched && stationForm.get('ip_address')?.invalid) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Enter a valid IPv4 address, for example 192.168.1.100.</p>
        </div>
      }

      <label class="atp-label atp-label--spaced" for="networkName">Display Name on Network</label>
      <input
        id="networkName"
        class="atp-input"
        type="text"
        autocomplete="off"
        placeholder="e.g. STATION-BAR-01"
        formControlName="network_display_name"
      />
      <p class="atp-help">How this station appears on the local network</p>
      @if (stationForm.get('network_display_name')?.errors?.['maxlength']) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Display name cannot exceed 50 characters.</p>
        </div>
      }
    </section>
  </div>
</form>
